/**
 * Gemini Service Layer
 * Transforms API responses for UI components
 * Mirrors copilotService structure for consistent dashboard experience
 */

import { geminiAPI } from '../api/geminiClient';
import { complianceAPI } from '../api/complianceClient';
import { enterpriseAIAPI } from '../api/enterpriseAIClient';
import { normalizeKnowledgeGap } from '../gemini-enterprise/normalizeKnowledgeGap';
import { getComplianceStatus, summarizeComplianceFrameworks } from '../gemini-enterprise/complianceScoring';

const finiteOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// Page date window + global filter bar params the Workspace backend accepts.
function workspaceScope(params = {}) {
  const scope = {};
  if (params.start_time) scope.start_time = params.start_time;
  if (params.end_time) scope.end_time = params.end_time;
  if (params.agent_name) scope.agent_name = params.agent_name;
  if (params.user_email) scope.user_email = params.user_email;
  return scope;
}

/**
 * Application distribution ({ docs: 12, gmail: 3 }) → the agent_breakdown rows
 * the shared pages render. Workspace activity has no success/outcome state, so
 * success_rate is null (shown as N/A), never inferred.
 */
function applicationBreakdown(distribution = {}) {
  const entries = Object.entries(distribution || {})
    .map(([application, count]) => [application, Number(count) || 0])
    .sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);
  return entries.map(([application, interactions]) => ({
    agent_name: getApplicationDisplayName(application),
    application,
    interactions,
    sessions: null,
    success_rate: null,
    pct_of_total: total > 0 ? Math.round((interactions / total) * 1000) / 10 : 0,
  }));
}

// Helper to format timestamp to relative time
function getRelativeTime(timestamp) {
  if (!timestamp) return 'Unknown';
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

/**
 * Service layer for Gemini dashboard and analytics
 */
export const geminiService = {
  /**
   * Get dashboard statistics
   */
  async getDashboardStats(params = {}) {
    try {
      const data = await geminiAPI.getDashboard(params);
      const applicationDistribution = data.application_distribution || {};
      return {
        total_interactions: data.total_interactions || 0,
        unique_users: data.unique_users || 0,
        unique_sessions: data.unique_sessions || 0,
        // Fields the shared dashboard / agent-network pages read. For Workspace
        // the "agents" are the Google applications Gemini was used in.
        active_agents: Object.keys(applicationDistribution).length,
        active_sessions: data.unique_sessions || 0,
        agent_breakdown: applicationBreakdown(applicationDistribution),
        // Workspace activity logs carry no outcome state and no grounding data:
        // null renders N/A instead of a fabricated rate.
        success_rate: null,
        avg_grounding_coverage: null,
        // The Workspace backend computes no prior-period comparison.
        previous_period: data.previous_period || null,
        behavioral_anomalies: data.behavioral_anomalies || 0,
        ip_threats: data.ip_threats || 0,
        interactions_with_threats: data.interactions_with_threats || 0,
        interactions_with_pii: data.interactions_with_pii || 0,
        knowledge_gaps: data.knowledge_gaps || 0,
        high_risk_intents: data.high_risk_intents || 0,
        severity_distribution: data.severity_distribution || {},
        application_distribution: data.application_distribution || {},
        gemini_action_distribution: data.gemini_action_distribution || {},
        event_category_distribution: data.event_category_distribution || {},
        feature_source_distribution: data.feature_source_distribution || {},
        timeline: data.timeline || [],
        critical_count: data.critical_count || 0,
        high_count: data.high_count || 0,
        medium_count: data.medium_count || 0,
        avg_minutes_per_interaction: finiteOrNull(data.avg_minutes_per_interaction),
        total_session_duration_minutes: data.total_session_duration_minutes || 0,
      };
    } catch (error) {
      console.error('Error fetching Gemini dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Get recent interactions for activity stream
   */
  async getRecentInteractions(params = {}) {
    try {
      const data = await geminiAPI.getInteractions({
        page_size: params.limit || 20,
        ...params,
      });
      return {
        interactions: (data.interactions || []).map((i) => ({
          interaction_id: i.interaction_id,
          timestamp: i.timestamp,
          user_email: i.user_email,
          application: i.application,
          intent_category: i.intent_category,
          risk_level: i.risk_level,
          threat_severity: i.threat_severity,
          has_pii: i.has_pii,
          session_id: i.session_id,
        })),
        total: data.total || 0,
      };
    } catch (error) {
      console.error('Error fetching recent interactions:', error);
      throw error;
    }
  },

  /**
   * Get threats list
   */
  async getThreats(params = {}) {
    try {
      const data = await geminiAPI.getThreats(params);
      return {
        threats: (data.threats || []).map((t) => ({
          interaction_id: t.interaction_id,
          timestamp: t.timestamp,
          user_email: t.user_email,
          application: t.application,
          agent_name: getApplicationDisplayName(t.application),
          severity: t.severity || t.threat_severity,
          intent_category: t.intent_category,
          risk_factors: t.risk_factors || [],
          has_pii: t.has_pii,
          entities_detected: t.entities_detected || [],
          detection_source: t.detection_source,
        })),
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 20,
        total_pages: data.total_pages || 1,
      };
    } catch (error) {
      console.error('Error fetching threats:', error);
      throw error;
    }
  },

  /**
   * Get threat summary by severity
   */
  async getThreatsSummary(params = {}) {
    try {
      const data = await geminiAPI.getThreatsSummary(params);
      return {
        critical: data.critical || 0,
        high: data.high || 0,
        medium: data.medium || 0,
        low: data.low || 0,
        total: data.total || 0,
      };
    } catch (error) {
      console.error('Error fetching threats summary:', error);
      throw error;
    }
  },

  /**
   * Get users with risk metrics
   */
  async getUsers(params = {}) {
    try {
      const data = await geminiAPI.getUsers(params);
      // Backend fields: interactions, threats, pii_detected, avg_risk_score,
      // applications (saf3ai-gemini-microservice _parse_users). Emit the shape
      // the shared Enterprise pages read, plus the legacy aliases.
      const users = (data.users || []).map((u) => {
        const interactions = u.interactions ?? u.total_interactions ?? 0;
        const threats = u.threats ?? u.threats_count ?? 0;
        const pii = u.pii_detected ?? u.pii_count ?? 0;
        const avgRisk = u.avg_risk_score ?? u.risk_score ?? 0;
        const applications = u.applications || u.top_applications || [];
        return {
          user_id: u.user_email,
          user_email: u.user_email,
          interactions,
          total_interactions: interactions,
          // null when the backend did not report sessions (older deployments).
          sessions: u.sessions ?? null,
          agents_used: applications.length,
          applications_used: applications,
          threats,
          threats_count: threats,
          critical_threats: u.critical_threats || 0,
          high_threats: u.high_threats || 0,
          medium_threats: u.medium_threats || 0,
          low_threats: u.low_threats || 0,
          pii_detected: pii,
          pii_count: pii,
          high_risk_queries: u.high_risk_queries || 0,
          // Average saf3ai.session.risk_score, a 0-1 score.
          avg_risk_score: avgRisk,
          risk_score: avgRisk,
          risk_level: u.risk_level || 'none',
          last_activity: u.last_activity,
          top_applications: applications,
          application_counts: u.application_counts || [],
          // Workspace activity has no outcome state.
          success_rate: null,
        };
      });
      const byRisk = data.risk_counts || {
        high: users.filter((u) => u.risk_level === 'high').length,
        medium: users.filter((u) => u.risk_level === 'medium').length,
        low: users.filter((u) => u.risk_level === 'low').length,
        none: users.filter((u) => u.risk_level === 'none').length,
      };
      return {
        users,
        total: data.total || 0,
        total_users: data.total_users ?? data.total ?? users.length,
        by_risk: byRisk,
        // Older backends do not send totals; the page then shows its own default.
        ...(data.threat_severity_totals ? { threat_severity_totals: data.threat_severity_totals } : {}),
        safe_users: byRisk.none ?? 0,
        page: data.page || 1,
        page_size: data.page_size || 20,
        total_pages: data.total_pages
          || Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 20))),
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get applications analytics
   */
  async getApplications(params = {}) {
    try {
      const data = await geminiAPI.getApplications(params);
      return {
        applications: (data.applications || []).map((app) => ({
          application: app.application || app.name,
          display_name: app.display_name || getApplicationDisplayName(app.application || app.name),
          icon: getApplicationIcon(app.application || app.name),
          total_interactions: app.total_interactions ?? app.interactions ?? 0,
          unique_users: app.unique_users ?? app.users ?? 0,
          unique_sessions: app.unique_sessions ?? 0,
          threats_count: app.threats_count ?? app.threats ?? 0,
          scans_completed: app.scans_completed ?? 0,
          threat_rate: app.threat_rate ?? null,
          last_active: app.last_active ?? null,
        })),
        total: data.total ?? data.summary?.total_applications ?? 0,
      };
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  },

  /**
   * Get sessions with threat scores
   */
  async getSessions(params = {}) {
    try {
      const data = await geminiAPI.getSessions(params);
      return {
        sessions: (data.sessions || []).map((s) => ({
          session_id: s.session_id,
          user_email: s.user_email,
          application: s.application,
          agent_name: s.application,
          display_name: getApplicationDisplayName(s.application),
          start_time: s.start_time,
          end_time: s.end_time,
          last_activity: s.end_time || s.start_time,
          duration_minutes: s.duration_minutes || 0,
          interactions: s.interactions || s.interaction_count || 0,
          interaction_count: s.interactions || s.interaction_count || 0,
          threats_count: s.threats_in_session || s.threats_count || 0,
          threats_in_session: s.threats_in_session || s.threats_count || 0,
          risk_score: s.risk_score || 0,
          // Workspace activity logs record no session outcome, so there is no
          // success rate to show (the old 100 − risk_score on a 0-1 risk scale
          // read ~100% for every session).
          success_rate: null,
        })),
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 20,
        total_pages: data.total_pages || 1,
      };
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  },

  async getSessionDetails(sessionId) {
    try {
      return await geminiAPI.getSessionDetails(sessionId);
    } catch (error) {
      console.error('Error fetching session details:', error);
      throw error;
    }
  },

  /**
   * Alias for getAuditLog — used by the shared Enterprise audit-log page.
   */
  async getAuditLogs(params = {}) {
    try {
      const data = await this.getAuditLog(params);
      return {
        logs: (data.logs || []).map(log => ({
          ...log,
          id: log.interaction_id || log.id,
          agent_name: log.application || log.agent_name || '',
          query: log.query || '',
          threat_flag: log.severity || 'none',
          event_type: log.event_type || 'interaction',
        })),
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 20,
        total_pages: data.total_pages || 1,
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  /**
   * Threat summary for the shared dashboard and agent network, in the same
   * shape as the other modules ({ total_flagged, critical, high, medium, low }).
   * Read from /dashboard (the Workspace backend has no /threats/summary).
   *
   * Only what the backend measures: severity counts come from its
   * severity_distribution (the terms aggregation omits empty buckets, so inside
   * a reported distribution an absent severity is a measured 0). Without a
   * distribution every count is null ("—"). Workspace classifies no threat
   * types (exfiltration, injection, external source), so none are reported —
   * critical_count is the intent risk level, not injection attempts.
   * A failed request rejects so the page shows "—" rather than zeros.
   */
  async getThreatStats(params = {}) {
    try {
      const data = await geminiAPI.getDashboard(workspaceScope(params));
      const reported = data?.severity_distribution;
      const hasDistribution = Boolean(reported) && typeof reported === 'object';
      const severity = {};
      ['critical', 'high', 'medium', 'low'].forEach((sev) => {
        severity[sev] = hasDistribution ? (finiteOrNull(reported[sev]) ?? 0) : null;
      });
      return {
        total_flagged: finiteOrNull(data?.interactions_with_threats),
        severity_distribution: hasDistribution ? severity : null,
        ...severity,
      };
    } catch (error) {
      console.error('Error fetching Gemini Workspace threat stats:', error);
      throw error;
    }
  },

  /**
   * Alias used by the shared Enterprise dashboard for recent activity.
   */
  async getConversations(params = {}) {
    try {
      const data = await this.getInteractions(params);
      return {
        conversations: (data.interactions || []).map(i => ({
          id: i.interaction_id,
          agent_name: i.application,
          user_email: i.user_email,
          timestamp: i.timestamp,
          threat_flag: i.threat_severity || 'none',
          intent_category: i.interaction_type || i.intent_category || '',
          query: '',
        })),
        total: data.total || 0,
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { conversations: [], total: 0 };
    }
  },

  /**
   * Maps workspace applications to the agent shape used by the Enterprise registry page.
   */
  async getAgents(params = {}) {
    try {
      const data = await this.getApplications(params);
      return {
        agents: (data.applications || []).map(a => ({
          agent_name: a.display_name || a.application,
          display_name: a.display_name || a.application,
          engine_id: a.application,
          total_interactions: a.total_interactions || 0,
          unique_sessions: a.unique_sessions || 0,
          // Workspace activity logs do not carry an agent execution state, so
          // threat-free rate must not be presented as a success rate.
          success_rate: null,
          last_active: a.last_active || null,
          threats: a.threats_count || 0,
          source_domains: [],
          methods: [],
          model: null,
        })),
      };
    } catch (error) {
      console.error('Error fetching agents (workspace):', error);
      throw error;
    }
  },

  /**
   * Stub — workspace has no recommendations endpoint; returns empty list so the
   * shared Enterprise knowledge-gaps page doesn't crash on the unguarded call.
   */
  async getRecommendations(_params = {}) {
    return { recommendations: [] };
  },

  /**
   * Stub — workspace compliance does not surface per-control audit logs.
   */
  async getComplianceAuditLogs(_controlId, _params = {}) {
    return [];
  },

  /**
   * Get intent analysis results with pagination
   */
  async getIntentAnalysis(params = {}) {
    try {
      // The Workspace route filters applications with `application_name`; the
      // shared page sends the filter bar's selection as `agent_name`.
      const { agent_name: agentName, ...rest } = params;
      const query = { ...rest };
      if (agentName && !query.application_name) query.application_name = agentName;
      const data = await geminiAPI.getIntentAnalysis(query);
      const categories = data.categories || [];
      const sampleIntents = data.sample_intents || data.intents || [];
      // The shared page pages through `intents` (Enterprise shape).
      const intents = sampleIntents.map((i) => ({
        ...i,
        conversation_id: i.interaction_id || i.conversation_id,
        interaction_id: i.interaction_id || i.conversation_id || '',
        user_email: i.user_email || '',
        agent_name: i.application ? getApplicationDisplayName(i.application) : (i.agent_name || ''),
        category: i.category || 'uncategorized',
        confidence: i.confidence || 0,
        query: '',
        risk_level: i.risk_level || 'none',
        threat_severity: i.threat_severity || 'none',
        threat_categories: Array.isArray(i.threat_categories) ? i.threat_categories : [],
        has_policy_violation: Boolean(i.has_policy_violation),
        content_status: i.content_status || null,
      }));
      return {
        summary: {
          ...(data.summary || {}),
          categories_detected: data.summary?.categories_detected ?? categories.length,
        },
        categories,
        distribution: Object.fromEntries(categories.map((c) => [c.category, c.count])),
        risk_distribution: data.risk_distribution || {},
        intents,
        sample_intents: sampleIntents,
        total: data.total || 0,
        total_pages: data.total_pages || 1,
        page: data.page || 1,
        page_size: data.page_size || 20,
      };
    } catch (error) {
      console.error('Error fetching intent analysis:', error);
      throw error;
    }
  },

  /**
   * Get knowledge gaps analysis
   */
  async getKnowledgeGaps(params = {}) {
    try {
      const data = await geminiAPI.getKnowledgeGaps(workspaceScope(params));
      // Backend: summary{total_gaps,…}, gap_types{type: n}, priorities{…},
      // top_topics, and (newer) gaps[] + gap_trend[]. Emit the shared page shape.
      const gaps = (data.gaps || []).map((gap, index) => {
        const agents = (gap.agents || []).map(getApplicationDisplayName);
        return normalizeKnowledgeGap({ ...gap, agents, agent_name: agents[0] || '' }, index);
      });
      const totalGaps = data.summary?.total_gaps ?? data.total_gaps ?? 0;
      const gapTypes = data.gap_types && !Array.isArray(data.gap_types) ? data.gap_types : {};
      return {
        gaps,
        total: gaps.length,
        stats: {
          total_gaps: totalGaps,
          high_priority: data.summary?.high_priority ?? data.priorities?.high ?? 0,
          ungrounded_queries: gaps.reduce((sum, g) => sum + (g.query_count || 0), 0),
          // Workspace records no grounding coverage, so there is no deficit to show.
          coverage_deficit: null,
        },
        gap_type_distribution: gapTypes,
        gap_trend: data.gap_trend || [],
        agent_ranking: [],
        user_impact: [],
        clustering: null,
        // Legacy keys
        gap_types: gapTypes,
        departments: data.departments || [],
        priorities: data.priorities || {},
        top_topics: data.top_topics || [],
        total_gaps: totalGaps,
      };
    } catch (error) {
      console.error('Error fetching knowledge gaps:', error);
      throw error;
    }
  },

  /**
   * Get AI-specific compliance data with frameworks
   * Returns full compliance structure for the compliance page with 100 controls
   * Matches Copilot compliance structure: OWASP LLM Top 10, Internal Security Policy,
   * GDPR, HIPAA, SOC 2, ISO 27001, NIST AI RMF, ISO 42001, EU AI Act, plus custom frameworks
   */
  async getCompliance(params = {}) {
    try {
      // Compliance MS supports time_range (last_24_hours, last_7_days, last_30_days, last_90_days)
      // or start_date/end_date ISO strings. Prefer time_range when available.
      const sourceFilter = { module: 'gemini-workspace' };
      if (params.time_range) {
        sourceFilter.time_range = params.time_range;
      } else {
        if (params.start_time) sourceFilter.start_date = params.start_time;
        if (params.end_time) sourceFilter.end_date = params.end_time;
        // Default to last 30 days if no date filter provided
        if (!sourceFilter.start_date && !sourceFilter.end_date) {
          sourceFilter.time_range = 'last_30_days';
        }
      }
      if (params.agent_id) sourceFilter.agent_id = params.agent_id;
      if (params.user_email) sourceFilter.user_email = params.user_email;

      // Compliance MS calls are the evidence; a failure there must surface as an
      // error, not as "0 violations / every control passing". The Workspace
      // dashboard is only the denominator for scan coverage.
      const [summaryData, controlSummaryData, frameworksData, dashboardData] = await Promise.all([
        complianceAPI.getComplianceSummary(sourceFilter),
        complianceAPI.getControlSummary(sourceFilter),
        complianceAPI.getAllFrameworks({ module: 'gemini-workspace', include_custom: true }),
        geminiAPI.getDashboard(params).catch(() => null),
      ]);

      const totalScanned = finiteOrNull(summaryData?.total_scanned) ?? 0;
      const totalInteractions = finiteOrNull(dashboardData?.total_interactions);
      const piiExposures = dashboardData ? (dashboardData.interactions_with_pii || 0) : null;
      const highRiskIntents = dashboardData ? (dashboardData.high_risk_intents || 0) : null;

      const summaryFrameworks = summaryData?.frameworks || [];
      const controlSummaries = controlSummaryData?.controls || [];
      const allFrameworks = Array.isArray(frameworksData) ? frameworksData : (frameworksData?.frameworks || []);

      const controlRowsFor = (fwId) => {
        const rows = new Map();
        controlSummaries
          .filter((ctrl) => String(ctrl.framework_id) === fwId)
          .forEach((ctrl) => rows.set(String(ctrl.control_id), ctrl));
        return rows;
      };

      // A control is scored only from its control-summary row. No row, or a row
      // with compliance_percentage null (no detection mapping), is "not
      // evaluated" — never a default pass/100.
      const scoreControl = (ctrl, row) => {
        if (!row || row.compliance_percentage == null) {
          return {
            ...ctrl,
            ...(row ? { name: row.control_name || ctrl.name } : {}),
            status: 'not-evaluated',
            score: null,
            violations: row ? (row.violations || 0) : 0,
            total_scanned: row ? (row.total_scanned ?? 0) : 0,
          };
        }
        const score = Number(row.compliance_percentage);
        return {
          ...ctrl,
          name: row.control_name || ctrl.name,
          // API returns 'violations' (not 'total_violations') for per-control data
          violations: row.violations || 0,
          score,
          status: score >= 90 ? 'pass' : score >= 70 ? 'warning' : 'fail',
          total_scanned: row.total_scanned ?? totalScanned,
        };
      };

      const frameworkMap = new Map();
      allFrameworks.forEach((fw) => {
        frameworkMap.set(String(fw.framework_id), {
          id: fw.is_custom ? fw.framework_id : `fw-${fw.framework_id}`,
          framework_id: fw.framework_id,
          name: fw.name,
          fullName: fw.name,
          description: fw.description || '',
          icon: fw.icon || 'shield',
          isCustom: fw.is_custom || false,
          orgSpecific: fw.is_custom || false,
          aiPlatform: 'gemini',
          controls: (fw.controls || []).map((ctrl) => ({
            id: ctrl.is_custom ? ctrl.control_id : `ctrl-${ctrl.control_id}`,
            control_id: ctrl.control_id,
            control_number: ctrl.control_number || String(ctrl.control_id),
            name: ctrl.name,
            description: ctrl.description || '',
          })),
          summary: null,
        });
      });

      // Custom frameworks that only exist in the summary (module-checked).
      summaryFrameworks.forEach((summaryFw) => {
        const fwId = String(summaryFw.framework_id);
        if (frameworkMap.has(fwId)) {
          frameworkMap.get(fwId).summary = summaryFw;
          return;
        }
        if (!summaryFw.is_custom || summaryFw.module !== 'gemini-workspace') return;
        frameworkMap.set(fwId, {
          id: summaryFw.framework_id,
          framework_id: summaryFw.framework_id,
          name: summaryFw.framework_name,
          fullName: summaryFw.framework_name,
          description: '',
          icon: 'shield',
          isCustom: true,
          orgSpecific: true,
          aiPlatform: 'gemini',
          controls: controlSummaries
            .filter((ctrl) => String(ctrl.framework_id) === fwId)
            .map((ctrl) => ({
              id: ctrl.control_id,
              control_id: ctrl.control_id,
              control_number: ctrl.control_number || String(ctrl.control_id),
              name: ctrl.control_name,
              description: '',
            })),
          summary: summaryFw,
        });
      });

      const frameworks = Array.from(frameworkMap.values()).map(({ summary, ...fw }) => {
        const rows = controlRowsFor(String(fw.framework_id));
        const controls = fw.controls.map((ctrl) => scoreControl(ctrl, rows.get(String(ctrl.control_id))));
        const evaluated = controls.filter((c) => c.status !== 'not-evaluated');
        const passing = evaluated.filter((c) => c.status === 'pass').length;
        // Score = controls passing / controls evaluated. Nothing evaluated → null
        // ("Not assessed"), never 100.
        const score = evaluated.length > 0 ? Math.round((passing / evaluated.length) * 100) : null;
        return {
          ...fw,
          controls,
          definedControls: controls.length,
          totalControls: evaluated.length,
          passingControls: passing,
          violations: controls.reduce((sum, c) => sum + (c.violations || 0), 0),
          score,
          status: score == null ? 'not-evaluated' : getComplianceStatus(score),
          total_scanned: summary?.total_scanned ?? totalScanned,
        };
      });

      // Headline = certified frameworks only (custom rules reported separately),
      // pooled controls passing / controls evaluated — same model as Enterprise.
      const { certified, custom } = summarizeComplianceFrameworks(frameworks);
      const overallScore = certified.overall_score;
      const overallStatus = overallScore == null ? 'not-assessed' : getComplianceStatus(overallScore);
      const totalViolations = certified.total_violations + custom.total_violations;
      // Measured coverage: interactions the compliance MS evaluated / Workspace
      // interactions in the same window. Unknown denominator → null.
      const scanCoverage = totalInteractions && totalInteractions > 0
        ? Math.min(100, Math.round((totalScanned / totalInteractions) * 1000) / 10)
        : null;
      const scoreNote = overallScore == null ? 'Not assessed: no certified controls evaluated in this window' : undefined;

      const stats = {
        overall_score: overallScore,
        overall_status: overallStatus,
        passing_controls: certified.passing_controls,
        total_controls: certified.total_controls,
        total_violations: totalViolations,
        scan_coverage: scanCoverage,
        total_scanned: totalScanned,
        certified: { ...certified, total_scanned: totalScanned },
        custom: { ...custom, total_scanned: totalScanned },
        score_basis: 'certified-controls-met',
        ...(scoreNote ? { score_note: scoreNote } : {}),
      };

      return {
        // Enterprise-shaped block the shared dashboard reads (`.stats`).
        stats,
        frameworks,
        violations: [],
        overall_score: overallScore,
        overall_status: overallStatus,
        score_note: scoreNote,
        total_controls: certified.total_controls,
        passing_controls: certified.passing_controls,
        total_violations: totalViolations,
        total_scanned: totalScanned,
        scan_coverage: scanCoverage,
        recent_violations: [],
        top_violators: [],
        metrics: {
          total_interactions: totalInteractions,
          total_scanned: totalScanned,
          scan_coverage: scanCoverage,
          threats_detected: highRiskIntents,
          pii_detected: piiExposures,
          // No document telemetry in Workspace activity logs.
          sensitive_docs: null,
        },
        pii_exposures: piiExposures,
        high_risk_intents: highRiskIntents,
        last_updated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching compliance:', error);
      throw error;
    }
  },
  /**
   * Get audit log entries
   */
  async getAuditLog(params = {}) {
    try {
      const data = await geminiAPI.getAuditLog(params);
      return {
        logs: (data.logs || []).map((log) => ({
          interaction_id: log.interaction_id,
          timestamp: log.timestamp,
          user_email: log.user_email,
          application: log.application,
          display_name: getApplicationDisplayName(log.application),
          event_type: log.event_type,
          severity: log.severity,
          status: log.status,
          documents_accessed: log.documents_accessed || [],
          risk_indicators: log.risk_indicators || [],
        })),
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 20,
        total_pages: data.total_pages || 1,
      };
    } catch (error) {
      console.error('Error fetching audit log:', error);
      throw error;
    }
  },

  /**
   * Get available filter options (applications + users) for the GeminiFilterBar.
   * Returns { agents: [appName, ...], users: [email, ...] } — "agents" key is intentional
   * to match the shared GeminiFilterBar/AgentFilterContext interface.
   */
  async getSessionFilters(params = {}) {
    try {
      const tp = {};
      if (params.start_time) tp.start_date = params.start_time;
      if (params.end_time) tp.end_date = params.end_time;
      if (!tp.start_date && !tp.end_date) tp.time_range = 'last_90_days';

      const [agentsRes, usersRes] = await Promise.all([
        complianceAPI.getAgents({ module: 'gemini-workspace', ...tp }).catch(() => ({ agents: [] })),
        complianceAPI.getUsers({ module: 'gemini-workspace', ...tp }).catch(() => ({ users: [] })),
      ]);

      const agents = (agentsRes?.agents || [])
        .map(a => a.agent_name || a.agent_display_name || a.agent_id)
        .filter(Boolean);
      const users = (usersRes?.users || [])
        .map(u => u.user_email)
        .filter(Boolean);

      // Fallback to the workspace-specific endpoint if compliance data isn't available yet
      if (!agents.length && !users.length) {
        const data = await geminiAPI.getSessionFilters(params).catch(() => ({}));
        return { agents: data.agents || [], users: data.users || [] };
      }

      return { agents, users };
    } catch (error) {
      console.debug('getSessionFilters not available:', error.message);
      return { agents: [], users: [] };
    }
  },

  /**
   * Get organization configuration
   */
  async getConfig(orgId = 'default') {
    try {
      const data = await geminiAPI.getConfig(orgId);
      return data;
    } catch (error) {
      console.error('Error fetching config:', error);
      throw error;
    }
  },

  /**
   * Update organization configuration
   */
  async updateConfig(orgId = 'default', config) {
    try {
      const data = await geminiAPI.updateConfig(orgId, config);
      return data;
    } catch (error) {
      console.error('Error updating config:', error);
      throw error;
    }
  },

  /**
   * Reset configuration to defaults
   */
  async resetConfig(orgId = 'default') {
    try {
      const data = await geminiAPI.resetConfig(orgId);
      return data;
    } catch (error) {
      console.error('Error resetting config:', error);
      throw error;
    }
  },

  /**
   * Get enhanced dashboard stats with security score
   */
  async getEnhancedDashboardStats(params = {}) {
    try {
      const [dashboardData, intentData, complianceData] = await Promise.all([
        geminiAPI.getDashboard(params),
        geminiAPI.getIntentAnalysis(params).catch(() => null),
        geminiAPI.getCompliance(params).catch(() => null)
      ]);

      // Use severity_distribution which combines all threat sources (intents, behavioral, IP threats)
      const severityDist = dashboardData.severity_distribution || {};
      const critical = severityDist.critical || 0;
      const high = severityDist.high || 0;
      const medium = severityDist.medium || 0;
      const low = severityDist.low || 0;

      // Calculate security score — only when there is activity to score; with
      // no interactions there is no evidence and the score is null, not 100.
      const total = dashboardData.total_interactions || 0;
      const threats = dashboardData.interactions_with_threats || 0;
      const pii = dashboardData.interactions_with_pii || 0;

      let securityScore = null;
      if (total > 0) {
        const threatPenalty = (threats / total) * 40;
        const piiPenalty = (pii / total) * 30;
        const criticalPenalty = critical * 5;
        const highPenalty = high * 3;
        securityScore = Math.max(0, Math.round(100 - threatPenalty - piiPenalty - criticalPenalty - highPenalty));
      }

      return {
        ...dashboardData,
        security_score: securityScore,
        security_grade: securityScore == null ? null : securityScore >= 90 ? 'A' : securityScore >= 80 ? 'B' : securityScore >= 70 ? 'C' : securityScore >= 60 ? 'D' : 'F',
        criticalCount: critical,
        highCount: high,
        mediumCount: medium,
        lowCount: low,
        intent_breakdown: intentData?.risk_distribution || {},
        jailbreak_attempts: intentData?.summary?.jailbreak_attempts || 0,
        risky_sessions: dashboardData.high_risk_intents || 0,
        total_sessions: dashboardData.unique_sessions || 0,
        // No compliance evidence → null ("Not assessed"), never a default score.
        compliance_score: finiteOrNull(complianceData?.overall_score)
      };
    } catch (error) {
      console.error('Error fetching enhanced dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Get session timeline
   */
  async getSessionTimeline(sessionId = null, params = {}) {
    try {
      const queryParams = sessionId ? { session_id: sessionId, ...params } : { ...params };
      const data = await geminiAPI.getSessions(queryParams);

      const sessions = (data.sessions || []).map(s => ({
        session_id: s.session_id,
        user_email: s.user_email,
        application: getApplicationDisplayName(s.application),
        start_time: s.start_time,
        end_time: s.end_time,
        interaction_count: s.interactions || s.interaction_count || 0,
        interactions: [],
        threats_in_session: s.threats_in_session || s.threats_count || 0,
        threat_count: s.threats_in_session || s.threats_count || 0,
        risk_score: Math.round((s.risk_score || 0) * 100),
        has_pii: false,
        duration_minutes: s.start_time && s.end_time
          ? Math.round((new Date(s.end_time) - new Date(s.start_time)) / 60000) || 1
          : 1
      }));

      return {
        sessions,
        total: data.total || sessions.length,
        high_risk_count: data.high_risk_count || sessions.filter(s => s.risk_score > 50).length,
        total_interactions: data.total_interactions || 0
      };
    } catch (error) {
      console.error('Error fetching session timeline:', error);
      throw error;
    }
  },

  /**
   * Get session flows for conversation reconstruction
   */
  async getSessionFlows(params = {}) {
    try {
      const data = await geminiAPI.getSessions({ page_size: 20, ...params });

      const sessions = (data.sessions || []).map(s => ({
        session_id: s.session_id,
        user: s.user_email || 'Unknown',
        user_email: s.user_email || '',
        app: getApplicationDisplayName(s.application),
        application: getApplicationDisplayName(s.application),
        messages: [],
        start_time: s.start_time,
        end_time: s.end_time,
        duration_sec: s.start_time && s.end_time
          ? Math.round((new Date(s.end_time) - new Date(s.start_time)) / 1000)
          : 0,
        message_count: s.interactions || s.interaction_count || 0,
        has_threat: (s.threats_in_session || s.threats_count || 0) > 0,
        has_pii: false,
        risk_score: s.risk_score || 0,
        time: getRelativeTime(s.end_time || s.start_time)
      }));

      const flows = sessions.map(s => ({
        user: s.user,
        user_email: s.user_email,
        application: s.app,
        has_threat: s.has_threat,
        time: s.time
      }));

      return {
        sessions,
        flows,
        total: data.total || sessions.length,
        high_risk_sessions: sessions.filter(s => s.risk_score >= 0.5).length,
        sessions_with_refusals: 0,
        sessions_with_sensitive_docs: 0
      };
    } catch (error) {
      console.error('Error fetching session flows:', error);
      throw error;
    }
  },

  /**
   * Get activity feed
   */
  async getActivityFeed(params = {}) {
    try {
      const [interactionsData, dashboardData] = await Promise.all([
        geminiAPI.getInteractions({ page_size: 15, ...params }),
        geminiAPI.getDashboard(params).catch(() => null)
      ]);

      const feed = (interactionsData.interactions || []).map(i => {
        const hasScanThreat = i.threat_severity && i.threat_severity !== 'none';
        const hasIntentThreat = i.risk_level && ['critical', 'high'].includes(i.risk_level);
        const isThreat = hasScanThreat || hasIntentThreat;
        const severity = i.threat_severity || i.risk_level || 'none';

        return {
          id: i.interaction_id,
          type: i.gemini_action || i.intent_category || 'interaction',
          user: i.user_email || 'Unknown',
          user_email: i.user_email || '',
          app: getApplicationDisplayName(i.application),
          preview: i.gemini_action?.replace(/_/g, ' ') || i.intent_category?.replace(/_/g, ' ') || 'Gemini interaction',
          timestamp: i.timestamp,
          is_threat: isThreat,
          threat_severity: severity,
          time_ago: getRelativeTime(i.timestamp)
        };
      });

      const items = feed.map(f => ({
        user: f.user,
        user_email: f.user_email,
        preview: f.preview,
        time_ago: f.time_ago,
        is_threat: f.is_threat
      }));

      const timeline = dashboardData?.timeline || [];
      const last7Days = timeline.slice(-7).map(t => t.count);

      return {
        feed,
        items,
        hourly_activity: [],
        current_active_users: new Set(feed.slice(0, 5).map(f => f.user)).size,
        threats_last_hour: feed.filter(f => f.is_threat).length,
        dailyCounts: last7Days.length > 0 ? last7Days : []
      };
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      throw error;
    }
  },

  /**
   * Get prompt patterns analysis
   */
  async getPromptPatterns(params = {}) {
    try {
      const [intentData, dashboardData] = await Promise.all([
        geminiAPI.getIntentAnalysis(params).catch(() => null),
        geminiAPI.getDashboard(params).catch(() => null)
      ]);

      const categories = intentData?.categories || [];
      const total = categories.reduce((sum, c) => sum + c.count, 0) || 0;

      // Map Gemini actions to pattern categories
      const actionDist = dashboardData?.gemini_action_distribution || {};
      const codeGen = categories.find(c => c.category === 'code_generation')?.count || 0;
      const infoSeek = categories.find(c => c.category === 'information_seeking')?.count || 0;
      const docAnalysis = (categories.find(c => c.category === 'document_analysis')?.count || 0) +
                          (categories.find(c => c.category === 'summarization')?.count || 0);

      return {
        patterns: {
          question_types: {},
          action_verbs: actionDist,
          sensitive_queries: [],
          document_requests: [],
          avg_prompt_length: 0,
          prompt_complexity: { simple: 0, moderate: 0, complex: 0 }
        },
        total_prompts: total,
        code_prompts: total > 0 ? Math.round((codeGen / total) * 100) : 0,
        query_prompts: total > 0 ? Math.round((infoSeek / total) * 100) : 0,
        creative_prompts: total > 0 ? Math.round((docAnalysis / total) * 100) : 0,
        code_count: codeGen,
        query_count: infoSeek,
        creative_count: docAnalysis,
        categories: categories,
        insights: {
          most_common_type: categories.length > 0 ? [categories[0].category, categories[0].count] : ['none', 0],
          sensitive_query_rate: '0',
          document_access_rate: '0'
        }
      };
    } catch (error) {
      console.error('Error fetching prompt patterns:', error);
      throw error;
    }
  },

  /**
   * Get data exposure insights
   */
  async getDataExposureInsights(params = {}) {
    try {
      const dashboardData = await geminiAPI.getDashboard(params);

      return {
        document_access: [],
        total_document_accesses: 0,
        file_type_breakdown: {},
        sensitivity_label_usage: {},
        pii_exposures: [],
        pii_count: dashboardData.interactions_with_pii || 0,
        // Workspace activity logs carry no sensitivity labels; these were
        // fixed fractions of other counts (pii × 0.3, total × 0.2).
        confidential_count: null,
        internal_count: null,
        safe_count: (dashboardData.total_interactions || 0) - (dashboardData.interactions_with_threats || 0),
        high_sensitivity_accesses: 0,
        risk_indicators: {
          sensitive_docs_accessed: 0,
          pii_in_prompts: dashboardData.interactions_with_pii || 0,
          threats_with_docs: 0
        }
      };
    } catch (error) {
      console.error('Error fetching data exposure:', error);
      throw error;
    }
  },

  /**
   * Get risk signals
   */
  async getRiskSignals(params = {}) {
    try {
      const [threatsData, usersData] = await Promise.all([
        geminiAPI.getThreats({ page_size: 20, ...params }),
        geminiAPI.getUsers({ page_size: 10, ...params })
      ]);

      const signals = (threatsData.threats || []).map(t => ({
        type: t.intent_category || 'threat',
        severity: t.severity || 'medium',
        user: t.user_email,
        timestamp: t.timestamp,
        detail: `${t.intent_category || 'Threat'} detected`,
        prompt_preview: ''
      }));

      const userRisks = (usersData.users || []).map(u => ({
        user: u.user_email,
        risk_score: u.avg_risk_score || u.risk_score || 0,
        breakdown: {
          threats: u.threats || u.threats_count || 0,
          pii: u.pii_detected || u.pii_count || 0,
          high_risk: u.high_risk_queries || 0
        }
      })).sort((a, b) => b.risk_score - a.risk_score);

      return {
        signals: signals.slice(0, 20),
        total_signals: threatsData.total || signals.length,
        by_severity: threatsData.severity_counts || {
          critical: signals.filter(s => s.severity === 'critical').length,
          high: signals.filter(s => s.severity === 'high').length,
          medium: signals.filter(s => s.severity === 'medium').length
        },
        user_risks: userRisks,
        top_risk_user: userRisks[0] || null
      };
    } catch (error) {
      console.error('Error fetching risk signals:', error);
      throw error;
    }
  },

  /**
   * Usage & ROI — the Enterprise `{ stats, … }` shape the shared dashboard,
   * Users and Usage & ROI pages read.
   *
   * Everything is measured from the Workspace backend except the two ROI
   * assumptions, which are the org's own settings (roi.avg_minutes_per_interaction,
   * roi.hourly_cost_rate from /config). If the config cannot be read, hours and
   * value stay null rather than falling back to invented constants. Workspace
   * has no outcome, grounding, feedback or complexity data, so those are null/empty.
   */
  async getUsageROI(params = {}) {
    try {
      const scope = workspaceScope(params);
      const [dashboardData, usersData, configData] = await Promise.all([
        geminiAPI.getDashboard(scope),
        geminiAPI.getUsers({ page_size: 10, ...scope }).catch(() => null),
        geminiAPI.getConfig().catch(() => null),
      ]);

      const configObj = configData?.config || configData || {};
      const roiConfig = configObj.roi || {};
      const displayConfig = configObj.display || {};

      const totalInteractions = dashboardData.total_interactions || 0;
      const uniqueUsers = dashboardData.unique_users || 0;
      const totalSessions = dashboardData.unique_sessions || 0;
      const applicationDistribution = dashboardData.application_distribution || {};

      const configuredMinutes = finiteOrNull(roiConfig.avg_minutes_per_interaction);
      const measuredMinutes = finiteOrNull(dashboardData.avg_minutes_per_interaction);
      const minutesPerQuery = roiConfig.use_calculated_avg && measuredMinutes != null
        ? measuredMinutes
        : configuredMinutes;
      const hourlyCostRate = finiteOrNull(roiConfig.hourly_cost_rate);

      const estimatedHoursSaved = minutesPerQuery != null
        ? Math.round(((totalInteractions * minutesPerQuery) / 60) * 100) / 100
        : null;
      const productivityValue = estimatedHoursSaved != null && hourlyCostRate != null
        ? Math.round(estimatedHoursSaved * hourlyCostRate)
        : null;

      const agentBreakdown = applicationBreakdown(applicationDistribution);

      const topUsers = (usersData?.users || []).slice(0, 5).map((u) => ({
        email: u.user_email,
        interactions: u.interactions ?? u.total_interactions ?? 0,
        risk_level: u.risk_level || 'none',
        avg_risk_score: u.avg_risk_score ?? u.risk_score ?? 0,
        // Real per-application counts when the backend sends them.
        app_usage: (u.application_counts || []).map((app) => ({
          name: getApplicationDisplayName(app.application),
          count: app.count || 0,
        })),
      }));

      return {
        stats: {
          total_interactions: totalInteractions,
          active_agents: agentBreakdown.length,
          unique_users: uniqueUsers,
          total_sessions: totalSessions,
          avg_queries_per_session: totalSessions > 0
            ? Math.round((totalInteractions / totalSessions) * 100) / 100
            : null,
          estimated_hours_saved: estimatedHoursSaved,
          productivity_value: productivityValue,
          // No complexity tiers in Workspace activity: value is not complexity-adjusted.
          complexity_adjusted_value: null,
          avg_complexity_weight: null,
          minutes_per_query: minutesPerQuery,
          hourly_cost_rate: hourlyCostRate,
          success_rate: null,
          avg_grounding_coverage: null,
        },
        display: {
          show_estimated_values: displayConfig.show_estimated_values !== false,
          show_grounding_bars: displayConfig.show_grounding_bars !== false,
        },
        // Real daily interaction counts ({ date, count }) for the window.
        monthly_trend: dashboardData.timeline || [],
        agent_breakdown: agentBreakdown,
        method_distribution: {},
        feedback: null,
        complexity_distribution: [],
        adoption: {
          unique_users: uniqueUsers,
          daily_active_users: [],
          hourly_distribution: [],
        },
        previous_period: null,
        // Legacy flat keys (pre-Enterprise shape)
        unique_users: uniqueUsers,
        estimated_value: productivityValue,
        top_users: topUsers,
        active_users_trend: [],
      };
    } catch (error) {
      console.error('Error fetching usage ROI:', error);
      throw error;
    }
  },

  /**
   * Get interactions with pagination
   */
  async getInteractions(params = {}) {
    try {
      const data = await geminiAPI.getInteractions({
        page: params.page || 1,
        page_size: params.limit || params.page_size || 20,
        user_email: params.user_email,
        application: params.application,
        start_time: params.start_time,
        end_time: params.end_time
      });

      const interactions = (data.interactions || []).map(i => ({
        interaction_id: i.interaction_id,
        timestamp: i.timestamp,
        user_email: i.user_email,
        application: getApplicationDisplayName(i.application),
        session_id: i.session_id,
        interaction_type: i.gemini_action || i.intent_category || 'unknown',
        threat_severity: i.threat_severity || 'none',
        has_pii: i.has_pii || false,
        scan_status: i.scan_status
      }));

      return {
        interactions,
        total: data.total || interactions.length,
        page: params.page || 1,
        limit: params.limit || 20,
        total_pages: Math.ceil((data.total || interactions.length) / (params.limit || 20)),
        stats: data.stats || {}
      };
    } catch (error) {
      console.error('Error fetching interactions:', error);
      throw error;
    }
  },

  // AI-driven conversation drift analysis. Sends the session's user prompt +
  // AI chunked responses to the AI microservice, which runs Gemini to detect
  // drift / hallucination / risk signals. Cached server-side by session_id so
  // repeated opens are instant.
  //
  // The endpoint is module-agnostic (it only requires session_id), so this is
  // the same call copilotService, copilotStudioService, and
  // geminiEnterpriseAgentsService make. SessionDetailContent gates the
  // "Saf3AI Conversation Analysis" card on this method existing, which is why
  // Gemini Workspace was the one module rendering the panel without it.
  async getConversationDriftAnalysis({ session_id, user_prompt, ai_responses, application, user_email, content_status, force_regenerate = false }) {
    try {
      return await enterpriseAIAPI.analyzeConversationDrift({
        session_id,
        user_prompt,
        ai_responses,
        application,
        user_email,
        content_status,
      }, { force_regenerate });
    } catch (err) {
      console.error('Error fetching drift analysis:', err);
      return { ai_summary: null, error: err?.message || 'Failed to load drift analysis' };
    }
  },
};

/**
 * Get display name for Google Workspace applications
 */
function getApplicationDisplayName(appCode) {
  const appNames = {
    gemini_app: 'Gemini App',
    docs: 'Google Docs',
    sheets: 'Google Sheets',
    slides: 'Google Slides',
    gmail: 'Gmail',
    meet: 'Google Meet',
    drive: 'Google Drive',
    calendar: 'Google Calendar',
    chat: 'Google Chat',
  };
  return appNames[appCode?.toLowerCase()] || appCode || 'Unknown';
}

/**
 * Get icon name for Google Workspace applications
 */
function getApplicationIcon(appCode) {
  const appIcons = {
    gemini_app: 'cpu',
    docs: 'file-text',
    sheets: 'grid',
    slides: 'layout',
    gmail: 'mail',
    meet: 'video',
    drive: 'folder',
    calendar: 'calendar',
    chat: 'message-circle',
  };
  return appIcons[appCode?.toLowerCase()] || 'box';
}

export default geminiService;
